import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getTrip } from '../lib/api';
import { getTripAccess } from '../lib/tripAccess';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, Calculator, EnvelopeSimple } from '@phosphor-icons/react';
import ContactForm from './ContactForm';

export default function CostCalculator() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const access = getTripAccess(code);
  const role = access?.role || (access?.legacy ? 'organiser' : null);
  const [trip, setTrip] = useState(null);
  const [showContact, setShowContact] = useState(false);

  const kind = searchParams.get('kind') || 'both';
  const initDeltaRounds = parseInt(searchParams.get('delta_rounds') || '1') || 1;
  const initDeltaPlayers = parseInt(searchParams.get('delta_players') || '0') || 0;

  const [deltaRounds, setDeltaRounds] = useState(initDeltaRounds);
  const [deltaPlayers, setDeltaPlayers] = useState(initDeltaPlayers);

  useEffect(() => {
    if (role !== 'organiser') { navigate(`/trip/${code}`); return; }
    getTrip(code).then(r => setTrip(r.data)).catch(() => navigate(`/trip/${code}`));
  }, [code, role, navigate]);

  if (!trip) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  const currentRounds = trip.rounds_created ?? trip.rounds?.length ?? 0;
  const currentPlayers = trip.players_created ?? trip.players?.length ?? 0;
  const maxR = trip.max_rounds || 0;
  const maxP = trip.max_players || 0;

  const effectiveDR = kind === 'players' ? 0 : Math.max(0, deltaRounds);
  const effectiveDP = kind === 'rounds' ? 0 : Math.max(0, deltaPlayers);

  // Defensive: if caps are missing (legacy trip or data integrity issue), show fallback
  const capsValid = maxR >= 1 && maxP >= 1;

  // Pricing formulas using trip-level caps (max_rounds, max_players)
  let cost = 0;
  if (capsValid) {
    if (effectiveDR > 0 && effectiveDP === 0) {
      cost = 2 * effectiveDR * maxP;
    } else if (effectiveDP > 0 && effectiveDR === 0) {
      cost = 2 * effectiveDP * maxR;
    } else {
      cost = 2 * ((maxR + effectiveDR) * (maxP + effectiveDP) - maxR * maxP);
    }
  }

  const costStr = `£${Math.max(0, cost).toFixed(2)}`;
  const msgBody = `Trip: ${trip.name} (${code})\nCurrent: ${maxR} rounds, ${maxP} players\nRequested: +${effectiveDR} rounds, +${effectiveDP} players\nEstimated cost: ${costStr}`;

  if (showContact) {
    return <ContactForm code={code} tripName={trip.name} prefillMessage={msgBody} onClose={() => setShowContact(false)} />;
  }

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="cost-calculator">
      <div className="p-4 flex items-center gap-3 max-w-lg mx-auto">
        <button onClick={() => navigate(`/trip/${code}`)} className="w-10 h-10 rounded-full bg-white border border-[#E1E6E4] flex items-center justify-center">
          <ArrowLeft className="w-5 h-5 text-[#1D242B]" weight="bold" />
        </button>
        <h1 className="text-xl font-bold text-[#1D242B]">Upgrade Trip</h1>
      </div>
      <div className="px-4 max-w-lg mx-auto space-y-4 pb-8">
        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-3">
          <h3 className="font-semibold text-[#1D242B]">Current Allowance</h3>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-[#F4F6F5] rounded-lg p-3">
              <p className="text-2xl font-bold text-[#1D242B]">{maxR}</p>
              <p className="text-xs text-[#455561]">Rounds ({currentRounds} created)</p>
            </div>
            <div className="bg-[#F4F6F5] rounded-lg p-3">
              <p className="text-2xl font-bold text-[#1D242B]">{maxP}</p>
              <p className="text-xs text-[#455561]">Players ({currentPlayers} created)</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-3">
          <h3 className="font-semibold text-[#1D242B]">Add Capacity</h3>
          {kind !== 'players' && (
            <div>
              <label className="text-sm text-[#455561] font-semibold block mb-1">Additional Rounds</label>
              <Input type="number" min={0} value={deltaRounds} onChange={(e) => setDeltaRounds(Math.max(0, parseInt(e.target.value) || 0))}
                className="h-12 rounded-xl" data-testid="delta-rounds-input" />
            </div>
          )}
          {kind !== 'rounds' && (
            <div>
              <label className="text-sm text-[#455561] font-semibold block mb-1">Additional Players</label>
              <Input type="number" min={0} value={deltaPlayers} onChange={(e) => setDeltaPlayers(Math.max(0, parseInt(e.target.value) || 0))}
                className="h-12 rounded-xl" data-testid="delta-players-input" />
            </div>
          )}
        </Card>

        <Card className="p-5 bg-[#1B4332] rounded-xl text-center" data-testid="cost-card">
          <Calculator className="w-8 h-8 text-[#E8E5DB] mx-auto mb-2" weight="bold" />
          {!capsValid ? (
            <p className="text-sm text-[#E8E5DB] leading-relaxed">Pricing unavailable — trip has no cap set.</p>
          ) : (
            <>
              <p className="text-3xl font-bold text-white">{costStr}</p>
              <p className="text-xs text-[#E8E5DB]/80 mt-1">Estimated pricing — final cost confirmed when you contact us.</p>
            </>
          )}
        </Card>

        <Button onClick={() => setShowContact(true)} disabled={!capsValid}
          className="w-full h-14 bg-[#E59000] hover:bg-[#CC8000] text-white rounded-xl text-base font-bold disabled:opacity-50" data-testid="contact-btn">
          <EnvelopeSimple className="w-5 h-5 mr-2" weight="bold" /> Contact Fore Tour
        </Button>
      </div>
    </div>
  );
}
